import { useFormik } from 'formik';
import {
  ChangeEvent, useEffect, useId, useState,
} from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import usePatchDriver from '../../../../API/hooks/drivers/usePatchDriver';
import { DriverDetails, PatchDriverRequest } from '../../../../API/types/Drivers';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import {
  emailValidator, nameValidator, phoneValidator,
} from '../../../../utils/formUtils';
import S, { EditDriverDatePickerGlobal } from './EditDriverStyles';
import useDriverThumbnail from '../../../../theme/useDriverThumbnail';
import ImagePreview from '../../../../components/common/ImagePreview/ImagePreview';
import ImageWithFallback from '../../../../components/common/ImageWithFallback/ImageWithFallback';
import Tags from '../../../../components/common/Tags';
import { generateLicenseExpiration, generatePreviewFullName, generatePreviewLicense } from '../../../Drivers/Modals/helpers';
import { FeaturesManagement } from '../../../../featuresControl';
import { DriverSettingsDefault } from '../../../../constants/DriverSettings';
import { getFormattedDateAndTime } from '../../../../utils/dateUtils';

type Props = {
  fleetId: string;
  driver?: DriverDetails;
  toggleModal: (closed: boolean) => void;
};

type DriverObj = {
  id: string | undefined;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  licenseName: string;
  // vehicleClassType: string;
  licenseExpireAt: string;
  file: any,
  deleteAvatar: boolean;
};

function EditDriver({ fleetId, driver, toggleModal }: Props): JSX.Element {
  const driverThumbnail = useDriverThumbnail();
  const licenseExpireAtId = useId();
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(undefined);
  const [removeAvatar, setRemoveAvatar] = useState(!driver?.avatarUrl);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePatchDriver(onSubmitSuccess, onSubmitFail);

  const licenseExpireAt = driver?.licenseExpireAt ? (new Date(driver.licenseExpireAt).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })) : null;

  const defaultValue: DriverObj = {
    id: driver?._id,
    firstName: driver?.firstName || '',
    lastName: driver?.lastName || '',
    email: driver?.email || '',
    phoneNumber: driver?.phoneNumber || '',
    licenseName: driver?.licenseName || '',
    // vehicleClassType: driver?.vehicleClassType || '',
    licenseExpireAt: licenseExpireAt || '',
    file: undefined,
    deleteAvatar: false,
  };

  const formik = useFormik({
    initialValues: defaultValue,
    onSubmit: (values) => mutate({
      ...values,
      licenseExpireAt: values.licenseExpireAt?.replace(/(\d+[/])(\d+[/])/, '$2$1'),
      workingHours: {},
      fleetId,
    } as unknown as PatchDriverRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.firstName.length) errors.firstName = 'Required';
      if (values.firstName.length && !nameValidator.test(values.firstName)) {
        errors.firstName = 'Can contain only letters';
      }
      if (!values.lastName.length) errors.lastName = 'Required';
      if (values.lastName.length && !nameValidator.test(values.lastName)) {
        errors.lastName = 'Can contain only letters';
      }

      if (!values.email.length) errors.email = 'Required';
      if (values.email.length && !emailValidator.test(values.email)) {
        errors.email = 'Invalid email';
      }

      if (!values.phoneNumber.length) errors.phoneNumber = 'Required';
      if (values.phoneNumber.length && !phoneValidator.test(values.phoneNumber)) {
        errors.phoneNumber = 'Invalid phone number';
      }
      if (!values.licenseName.length) errors.licenseName = 'Required';
      // if (!values.vehicleClassType.length) errors.vehicleClassType = 'Required';
      if (!values.licenseExpireAt.length) errors.licenseExpireAt = 'Required';
      if (values.file && (values.file.size > DriverSettingsDefault.avatarMaxSize)) {
        errors.file = `Maximum size 250KB is allowed (actually ${Math.round(values.file.size / 1024)} KB)`;
      }
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const handleAvatarChange = (e: ChangeEvent<any>): void => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (event) {
        const imageDataURL = event?.target?.result;
        setAvatarPreview(imageDataURL as string);
        formik.setFieldValue('file', file);
      };
      reader.readAsDataURL(file);
      setRemoveAvatar(false);
    } else {
      formik.setFieldValue('file', undefined);
    }
  };

  const handleRemoveAvatar = (): void => {
    formik.setFieldValue('deleteAvatar', true);
    setRemoveAvatar(true);
    setAvatarPreview(undefined);
  };

  const avatarSource = avatarPreview || (!removeAvatar && driver?.avatarUrl) || '';

  return (
    <>
      <EditDriverDatePickerGlobal />
      <S.Wrapper>
        <form onSubmit={formik.handleSubmit}>
          <div className="details-container">
            <h4 className="form-title"> Personal details </h4>
            <S.RowContainer>
              <Input
                label="First name"
                name="firstName"
                placeholder="George"
                defaultValue={driver?.firstName}
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                error={formik.errors.firstName}
              />
              <Input
                label="Surname"
                name="lastName"
                placeholder="Lopez"
                defaultValue={driver?.lastName}
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                error={formik.errors.lastName}
              />
            </S.RowContainer>
            <S.RowContainer>
              <Input
                label="Email"
                name="email"
                placeholder="yourname@email.com"
                defaultValue={driver?.email}
                type="email"
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={formik.errors.email}
              />
              <Input
                label="Phone number"
                name="phoneNumber"
                placeholder="0124447778888"
                defaultValue={driver?.phoneNumber}
                type="tel"
                maxLength={13}
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.phoneNumber}
                error={formik.errors.phoneNumber}
              />
            </S.RowContainer>
            <S.RowContainer>
              <Input
                label="License"
                name="licenseName"
                onChange={handleInputChange}
                placeholder="HG07087987D344"
                defaultValue={driver?.licenseName}
                onBlur={formik.handleBlur}
                value={formik.values.licenseName}
                error={formik.errors.licenseName}
              />
              <S.DatePickerWrapper error={!!formik.errors.licenseExpireAt}>
                <label htmlFor={licenseExpireAtId} className="date-label">License expiration date</label>
                <ReactDatePicker
                  id={licenseExpireAtId}
                  selected={formik.values.licenseExpireAt ? new Date(formik.values.licenseExpireAt.split('/').reverse().join('-')) : null}
                  onChange={(date: Date | null) => {
                    const value = date ? date.toLocaleDateString('en-GB') : '';
                    formik.setFieldValue('licenseExpireAt', value, true).then(() => {
                      formik.setFieldTouched('licenseExpireAt', true, false);
                    });
                    setSubmitError('');
                  }}
                  onBlur={formik.handleBlur}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="dd/mm/yyyy"
                  showPopperArrow={false}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="scroll"
                  yearDropdownItemNumber={20}
                  popperClassName="driver-license-datepicker-popper"
                  className="date-input"
                  wrapperClassName="date-input-wrapper"
                />
                {formik.errors.licenseExpireAt && (
                  <span className="date-error">{formik.errors.licenseExpireAt}</span>
                )}
              </S.DatePickerWrapper>
            </S.RowContainer>
            {FeaturesManagement.setting.avatarUploading && (
              <S.RowContainer>
                <Input
                  label="Avatar"
                  type="file"
                  name="file"
                  placeholder="Select photo"
                  onChange={handleAvatarChange}
                  accept="image/png, image/jpeg"
                  error={formik.errors.file as string}
                />
                <button type="button" className={removeAvatar ? 'remove-avatar blocked' : 'remove-avatar'} onClick={handleRemoveAvatar}>Remove avatar</button>
              </S.RowContainer>
            )}
            <S.RowContainer>
              <h4 className="driver-info">
                Driver was added at {driver?.createdAt ? getFormattedDateAndTime(driver?.createdAt) : 'N/A'}. Last update: {driver?.updatedAt ? getFormattedDateAndTime(driver.updatedAt) : 'N/A'}
              </h4>
            </S.RowContainer>
            {!!submitError && (
              typeof submitError === 'string'
                ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
                : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
            )}
          </div>
          <div className="preview-container">
            <div className="preview-card">
              <h4 className="preview-card-title"> Driver preview </h4>
              <div className="preview-card-tags-container">
                {avatarSource ? (
                  <button
                    type="button"
                    className="avatar-button"
                    title="View driver photo full size"
                    aria-label="View driver photo full size"
                    onClick={() => setPhotoPreviewOpen(true)}
                  >
                    <ImageWithFallback src={avatarSource} alt="driver" fallbackSrc={driverThumbnail} />
                  </button>
                ) : (
                  <img src={driverThumbnail} alt="driver" />
                )}
                <p className="name">
                  {generatePreviewFullName(formik.values.firstName, formik.values.lastName)}
                </p>
                <p className="license"> {generatePreviewLicense(formik.values.licenseName)} </p>
                {/* <Tags.LabeledIcon label="Add license details" icon={IconKeys.CAR_HATCHBACK} /> */}
                {/* <Tags.LabeledIcon label="Add schedule" icon={IconKeys.CLOCK} /> */}
                <Tags.LabeledIcon
                  label={generateLicenseExpiration(formik.values.licenseExpireAt)}
                  icon={IconKeys.CLIPBOARD_PLUS}
                />
              </div>
              <div className="preview-card-buttons-container">
                <Button
                  height="50px"
                  onClick={() => toggleModal(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  className="add-button"
                  height="50px"
                  type="submit"
                  disabled={!formik.dirty || formik.isSubmitting || !formik.isValid || !!submitError}
                >
                  Update
                </Button>
              </div>
            </div>
          </div>
        </form>
      </S.Wrapper>
      {photoPreviewOpen && !!avatarSource && (
        <ImagePreview
          src={avatarSource}
          alt="Driver photo"
          onClose={() => setPhotoPreviewOpen(false)}
        />
      )}
    </>
  );
}

export default EditDriver;
